import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
  type FormEvent,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { toast } from "sonner";
import { AgentChat } from "@/components/AgentChat";
import { AgentRevisionHistory } from "@/components/AgentRevisionHistory";
import AgentForm from "@/components/AgentForm";
import { EntityAvatar } from "@/components/EntityAvatar";
import { FormSection } from "@/components/entity-page/FormSection";
import { ConfigField } from "@/components/entity-page/ConfigField";
import { DeleteConfirmPanel } from "@/components/entity-page/DeleteConfirmPanel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, MessageSquare, Phone, Filter, Pencil, Trash2, Copy } from "lucide-react";
import { fetchClient, deleteAgent, getTranscripts } from "@/lib/api";
import type {
  TranscriptConversation,
  TranscriptSessionResponse,
} from "@/lib/api";
import type { SerializedAgent, SerializedIntegration, SerializedVoiceAgent } from "@/types";
import {
  getChatAgentUrl,
  getChatAgentWebSocketUrl,
  getDefaultDomain,
} from "@/lib/chatAgentUrl";
/**
 * typeId is the authoritative signal for chat vs. voice - SerializedAgent
 * isn't a true discriminated union (typeId's type doesn't vary per member),
 * so this type predicate is what lets TypeScript narrow to
 * SerializedVoiceAgent instead of relying on optional-property presence
 * (e.g. "scheduling"/"twilioPhoneNumber" in agent) as a structural stand-in.
 */
/**
 * The number to show for a conversation: an outbound call's fromNumber is the
 * agent's own Twilio number, so prefer the customer's. Calls from before that
 * was persisted only carry fromNumber.
 */
function callerNumber(conv: TranscriptConversation): string | undefined {
  return conv.customerNumber || conv.fromNumber;
}

function isVoiceAgentType(agent: SerializedAgent): agent is SerializedVoiceAgent {
  return !agent.typeId?.toLowerCase().includes("chat");
}

/**
 * Resolves a voice agent's scheduling.calendly.integrationId to the
 * integration it points at (by name), flagging when that reference is
 * missing, unset, or points at a deleted/deactivated integration - mirrors
 * the staleness check the agents list page's "Missing Config" badge uses.
 */
function resolveCalendlySummary(
  agent: SerializedAgent,
  integrations: SerializedIntegration[]
): { name: string; isStale: boolean } | null {
  if (!isVoiceAgentType(agent) || agent.scheduling?.provider !== "calendly") {
    return null;
  }
  const integrationId = agent.scheduling.calendly?.integrationId;
  if (!integrationId) {
    return { name: "Not selected", isStale: true };
  }
  const match = integrations.find(
    (integration) => integration.id === integrationId && integration.typeId === "calendly" && integration.isActive
  );
  return match ? { name: match.name, isStale: false } : { name: integrationId, isStale: true };
}

interface AgentChatMessage {
  type: "user" | "ai" | "error";
  content: string;
}

export const Route = createFileRoute("/dashboard/clients/$id/agents/$agentId")({
  component: AgentPage,
});

// Export for testing
export function AgentPageComponent({
  businessId,
  agentId,
}: {
  businessId: string;
  agentId: string;
}) {
  return <AgentPageInner businessId={businessId} agentId={agentId} />;
}

function AgentPage() {
  const params = Route.useParams();
  const { id, agentId } = params;

  // Debug logging
  if (typeof window !== "undefined") {
    console.log("AgentPage params:", { id, agentId });
  }

  if (!id || !agentId) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>
              <p>Missing route parameters</p>
              <p className="text-sm text-muted-foreground">
                id: {id || "missing"}, agentId: {agentId || "missing"}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return <AgentPageInner businessId={id} agentId={agentId} />;
}

function AgentPageInner({
  businessId,
  agentId,
}: {
  businessId: string;
  agentId: string;
}) {
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [messages, setMessages] = useState<AgentChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [connectionError, setConnectionError] = useState("");
  const errorHandledRef = useRef(false);
  const [publicToken, setPublicToken] = useState("");
  const [domain, setDomain] = useState(getDefaultDomain());
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);

  // Agent data
  const [agent, setAgent] = useState<SerializedAgent | null>(null);
  const [agentLoading, setAgentLoading] = useState(true);
  const [integrations, setIntegrations] = useState<SerializedIntegration[]>([]);

  // Transcripts
  const [transcripts, setTranscripts] = useState<TranscriptConversation[]>([]);
  const [selectedSession, setSelectedSession] =
    useState<TranscriptSessionResponse | null>(null);
  const [transcriptsLoading, setTranscriptsLoading] = useState(false);
  const [transcriptsError, setTranscriptsError] = useState<string | null>(null);

  // Filters
  const [channelFilter, setChannelFilter] = useState<"chat" | "voice" | "sms" | "all">(
    "all"
  );
  // createdAt, not lastMessageAt: lastMessageAt is only written once a message
  // is recorded, so a call that produced none has no value for it. Sorting on a
  // field a document lacks pushes it to the bottom in the agent-scoped path and
  // makes Firestore omit it entirely in the business-wide orderBy - which hid
  // exactly the calls worth seeing (hangup during the greeting, failed transfer
  // before the first turn, setup error). createdAt is stamped at setup, so every
  // conversation has one. DRA-358.
  const [timeField, setTimeField] = useState<
    "createdAt" | "updatedAt" | "lastMessageAt" | "endedAt"
  >("createdAt");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [limit, setLimit] = useState(50);
  const [minTurns, setMinTurns] = useState<number | undefined>(undefined);

  const navigate = useNavigate();
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const latestQueryId = useRef(0);

  // Load agent details. loadAgent is called again after a successful edit
  // (see onSaved below), so guard against an earlier in-flight fetchClient
  // call resolving after a later one and overwriting fresh data with stale
  // data - and reset agent to null (not just leave the last-loaded value) if
  // the id isn't found in the response, so "Agent not found" fires instead
  // of silently keeping stale data on screen.
  const loadAgent = useCallback(async () => {
    const queryId = ++latestQueryId.current;
    try {
      const response = await fetchClient(businessId);
      if (queryId !== latestQueryId.current) return;
      const foundAgent = response.agents.find((a) => a.id === agentId);
      if (foundAgent) {
        setAgent(foundAgent);
        if (foundAgent.publicToken) {
          setPublicToken(foundAgent.publicToken);
        }
      } else {
        setAgent(null);
      }
      setIntegrations(response.integrations || []);
      // Set domain to first allowed domain from client config, or fallback to default
      if (
        response.client.allowedDomains &&
        response.client.allowedDomains.length > 0
      ) {
        setDomain(response.client.allowedDomains[0]);
      } else {
        setDomain(getDefaultDomain());
      }
    } catch (error) {
      console.error("Error loading agent:", error);
    } finally {
      if (queryId === latestQueryId.current) {
        setAgentLoading(false);
      }
    }
  }, [businessId, agentId]);

  useEffect(() => {
    loadAgent();
    return () => {
      latestQueryId.current = -1; // invalidate any in-flight query on unmount
    };
  }, [loadAgent]);

  const handleDelete = async () => {
    if (!agent) return;
    try {
      const result = await deleteAgent(agent.id);
      if (result.success) {
        toast.success("Agent deleted successfully");
        navigate({ to: "/dashboard/clients/$id", params: { id: businessId } });
      } else {
        toast.error("Failed to delete agent", { description: result.error });
      }
    } catch (error) {
      console.error("Error deleting agent:", error);
      toast.error("Failed to delete agent", {
        description: error instanceof Error ? error.message : "Unknown error",
      });
    }
  };

  const handleCopyToken = async (token: string) => {
    if (!token) {
      toast.error("No public token to copy");
      return;
    }
    if (!navigator.clipboard) {
      toast.error("Clipboard API not supported in this browser");
      return;
    }
    try {
      await navigator.clipboard.writeText(token);
      toast.success("Public token copied to clipboard");
    } catch (error) {
      console.error("Failed to copy token:", error);
      toast.error("Failed to copy public token");
    }
  };

  // Load transcripts
  const loadTranscripts = useCallback(async () => {
    setTranscriptsLoading(true);
    setTranscriptsError(null);
    try {
      const params: Parameters<typeof getTranscripts>[0] = {
        businessId,
        agentId,
        channel: channelFilter !== "all" ? channelFilter : undefined,
        timeField,
        order,
        limit,
      };
      if (fromDate) {
        params.from = fromDate;
      }
      if (toDate) {
        params.to = toDate;
      }
      if (minTurns !== undefined) {
        params.minTurns = minTurns;
      }

      const result = await getTranscripts(params);
      if ("conversations" in result) {
        setTranscripts(result.conversations);
      } else {
        // Single session response
        setTranscripts([result.conversation]);
        setSelectedSession(result);
      }
    } catch (error) {
      setTranscriptsError(
        error instanceof Error ? error.message : "Failed to load transcripts"
      );
    } finally {
      setTranscriptsLoading(false);
    }
  }, [
    businessId,
    channelFilter,
    timeField,
    order,
    fromDate,
    toDate,
    limit,
    minTurns,
    agentId,
  ]);

  useEffect(() => {
    if (businessId) {
      loadTranscripts();
    }
  }, [businessId, loadTranscripts]);

  const loadSessionDetails = async (sessionId: string) => {
    try {
      const result = await getTranscripts({
        businessId,
        sessionId,
        includeMessages: true,
        messageLimit: 5000,
      });
      if ("conversation" in result) {
        setSelectedSession(result);
      }
    } catch (error) {
      console.error("Error loading session details:", error);
    }
  };

  useEffect(() => {
    if (!agentId || !sessionToken) return;

    // Reset error tracking when attempting to connect
    errorHandledRef.current = false;

    const wsUrl = getChatAgentWebSocketUrl();
    const socket = new WebSocket(
      `${wsUrl}?token=${encodeURIComponent(sessionToken)}`
    );

    socket.onopen = () => {
      // Set WebSocket state in callback to avoid synchronous setState in effect
      setWs(socket);
      setConnectionError("");
      errorHandledRef.current = false;
    };

    socket.onerror = (error) => {
      console.error("WebSocket error:", error);
      if (!errorHandledRef.current) {
        setConnectionError(
          "Failed to connect to the chat server. Please ensure the server is running."
        );
        errorHandledRef.current = true;
      }
      setWs(null);
    };

    socket.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "ai-response") {
        setMessages((prev) => [...prev, { type: "ai", content: data.content }]);
      } else if (data.type === "error") {
        setMessages((prev) => [
          ...prev,
          { type: "error", content: data.content },
        ]);
      }
    };

    socket.onclose = (event) => {
      setWs(null);
      // If the connection was closed unexpectedly (not cleanly), show an error
      if (!event.wasClean && !errorHandledRef.current) {
        setConnectionError(
          "Connection to the chat server was lost. Please refresh the page to reconnect."
        );
        errorHandledRef.current = true;
      }
    };

    // Cleanup on unmount
    return () => {
      if (
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
      ) {
        socket.close();
      }
    };
  }, [agentId, sessionToken]);

  const handleStartSession = async () => {
    if (!publicToken) {
      setConnectionError("Public token is required to start a session.");
      return;
    }
    setIsConnecting(true);
    setConnectionError("");
    setMessages([]);
    try {
      const chatAgentUrl = getChatAgentUrl();
      const sessionDomain =
        typeof window !== "undefined" ? window.location.hostname : domain;
      const response = await fetch(`${chatAgentUrl}/session`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          publicToken: publicToken.trim(),
          domain: sessionDomain,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || "Failed to create session");
      }
      setSessionToken(data.token);
    } catch (error) {
      console.error("Failed to start session:", error);
      setConnectionError(
        error instanceof Error ? error.message : "Unable to start session."
      );
    } finally {
      setIsConnecting(false);
    }
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!input || !input.trim()) {
      return; // Don't send empty messages
    }
    if (!ws) {
      setConnectionError(
        "WebSocket is not connected. Please wait for the connection to establish."
      );
      return;
    }
    if (ws.readyState !== WebSocket.OPEN) {
      setConnectionError(
        `WebSocket is not ready (state: ${ws.readyState}). Please wait for the connection to establish.`
      );
      return;
    }
    try {
      ws.send(input.trim());
      setMessages((prev) => [...prev, { type: "user", content: input.trim() }]);
      setInput("");
      setConnectionError(""); // Clear any previous errors on successful send
    } catch (error) {
      console.error("Failed to send message:", error);
      setConnectionError(
        error instanceof Error ? error.message : "Failed to send message"
      );
    }
  };

  if (agentLoading) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Loading agent...</div>
          </div>
        </div>
      </div>
    );
  }

  if (!agent) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div>Agent not found</div>
          </div>
        </div>
      </div>
    );
  }

  // Voice agents (inbound/outbound) don't have a chat-agent WebSocket session to test and
  // don't use publicToken/widget embedding. Discriminate by typeId (via
  // isVoiceAgentType), not the presence of twilioPhoneNumber - that field is
  // required on VoiceAgent's type but isn't enforced at creation time, so a
  // voice agent without a number assigned yet would have the key missing from
  // the Firestore doc entirely, making an `in` check misclassify it as chat.
  const isVoiceAgent = isVoiceAgentType(agent);
  const voiceAgent = isVoiceAgent ? agent : null;
  const calendlySummary = resolveCalendlySummary(agent, integrations);

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to="/dashboard/clients/$id"
              params={{ id: businessId }}
              className="inline-block"
            >
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Client
              </Button>
            </Link>
            <EntityAvatar type="agent" icon={agent.icon} name={agent.name} className="w-12 h-12" />
            <div>
              <h1 className="text-3xl font-bold">{agent.name}</h1>
              {agent.description && (
                <p className="text-muted-foreground mt-1">
                  {agent.description}
                </p>
              )}
            </div>
          </div>
          {!showDeleteConfirm && (
            <div className="flex items-center gap-2">
              {mode === "view" ? (
                <Button type="button" variant="outline" onClick={() => setMode("edit")}>
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              ) : (
                <Button type="button" variant="outline" onClick={() => setMode("view")}>
                  Cancel
                </Button>
              )}
              <Button
                type="button"
                variant="destructive"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </div>
          )}
        </div>

        {/* Configuration summary (DRA-264) - every configured field, grouped
            the same way AgentForm's edit page groups them, so nothing is
            hidden behind reopening the edit form. Edit mode (DRA-285) swaps
            this whole block for AgentForm; delete-confirm swaps it for the
            shared DeleteConfirmPanel. Chat Testing / Transcripts below are
            orthogonal to config editing and stay visible in every mode. */}
        {showDeleteConfirm ? (
          <DeleteConfirmPanel
            title="Delete Agent?"
            description="This will permanently delete the agent and all associated data."
            confirmLabel="Delete Agent"
            onCancel={() => setShowDeleteConfirm(false)}
            onConfirm={handleDelete}
          />
        ) : mode === "edit" ? (
          <AgentForm
            businessId={businessId}
            agent={agent}
            integrations={integrations}
            onSaved={() => {
              loadAgent();
              setMode("view");
            }}
          />
        ) : (
          <div className="space-y-6">
            <FormSection title="Identity">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <ConfigField label="Agent Type" value={agent.typeId} />
                <ConfigField label="Status" value={agent.isActive ? "Active" : "Inactive"} />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground">System Prompt</p>
                {agent.systemPrompt ? (
                  <pre className="mt-1 max-h-48 overflow-y-auto whitespace-pre-wrap rounded-md bg-muted p-3 font-mono text-xs">
                    {agent.systemPrompt}
                  </pre>
                ) : (
                  <p className="text-sm italic text-muted-foreground">Not set</p>
                )}
              </div>
            </FormSection>

            <FormSection title="Model & Provider">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <ConfigField label="Model" value={agent.modelId} mono />
                <ConfigField label="Provider" value={agent.providerId} mono />
                {voiceAgent ? (
                  <ConfigField label="Voice" value={voiceAgent.voiceId} mono />
                ) : (
                  "modelSettings" in agent &&
                  agent.modelSettings && (
                    <div className="md:col-span-2">
                      <p className="text-xs font-medium text-muted-foreground">Model Settings</p>
                      <pre className="mt-1 max-h-32 overflow-y-auto whitespace-pre-wrap rounded-md bg-muted p-3 font-mono text-xs">
                        {agent.modelSettings}
                      </pre>
                    </div>
                  )
                )}
              </div>
            </FormSection>

            {voiceAgent && (
              <>
                <FormSection title="Voice-agent basics">
                  <ConfigField label="Twilio Phone Number" value={voiceAgent.twilioPhoneNumber} mono />
                  {voiceAgent.twilioPhoneNumber && (
                    <ConfigField
                      label="Twilio Verification"
                      value={
                        voiceAgent.twilioPhoneNumberSid
                          ? [
                              "Verified",
                              [
                                voiceAgent.twilioPhoneNumberCapabilities?.voice && "voice",
                                voiceAgent.twilioPhoneNumberCapabilities?.sms && "sms",
                                voiceAgent.twilioPhoneNumberCapabilities?.mms && "mms",
                              ]
                                .filter(Boolean)
                                .join(", "),
                            ]
                              .filter(Boolean)
                              .join(" — ")
                          : "Not verified — edit the agent and click Verify"
                      }
                    />
                  )}
                </FormSection>

                <FormSection title="Call Agent Settings">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ConfigField label="Initial Greeting" value={voiceAgent.initialGreeting} />
                    <ConfigField label="Transcription Provider" value={voiceAgent.transcriptionProvider} />
                    <ConfigField label="Language" value={voiceAgent.language} />
                  </div>
                  <div className="border-t pt-4">
                    <p className="mb-3 text-sm font-medium text-foreground">Scheduling</p>
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                      <ConfigField label="Provider" value={voiceAgent.scheduling?.provider ?? "none"} />
                      {calendlySummary && (
                        <ConfigField
                          label="Calendly Integration"
                          value={
                            calendlySummary.isStale
                              ? `${calendlySummary.name} (missing or deactivated)`
                              : calendlySummary.name
                          }
                        />
                      )}
                    </div>
                  </div>
                </FormSection>
              </>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Chat Testing (chat agents) / Voice Agent testing note (voice agents) */}
          {isVoiceAgent ? (
            <Card>
              <CardHeader>
                <CardTitle>Voice Agent</CardTitle>
                <CardDescription>
                  This agent is reached by calling its Twilio number - there
                  is no chat session to test here. Full configuration is
                  above.
                </CardDescription>
              </CardHeader>
            </Card>
          ) : (
            <Card>
              <CardHeader>
                <CardTitle>Test Chat</CardTitle>
                <CardDescription>
                  Test the agent by starting a chat session
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="publicToken">Agent Public Token</Label>
                  <div className="flex gap-2">
                    <Input
                      id="publicToken"
                      value={publicToken}
                      onChange={(e) => setPublicToken(e.target.value)}
                      placeholder="Agent public token"
                    />
                    <Button
                      type="button"
                      variant="secondary"
                      aria-label="Copy public token"
                      onClick={() => handleCopyToken(publicToken)}
                      disabled={!publicToken}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="domain">Domain</Label>
                  <Input
                    id="domain"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    placeholder="example.com"
                  />
                </div>
                <Button
                  onClick={handleStartSession}
                  disabled={isConnecting || !publicToken}
                  className="w-full"
                >
                  {isConnecting ? "Connecting..." : "Start Session"}
                </Button>
                {connectionError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <p className="text-red-800 font-semibold mb-1">
                      Connection Error
                    </p>
                    <p className="text-red-600 text-sm">{connectionError}</p>
                  </div>
                )}
                {sessionToken && !connectionError && (
                  <div className="border rounded-lg p-4">
                    <AgentChat
                      messages={messages}
                      input={input}
                      setInput={setInput}
                      onSubmit={handleSubmit}
                      disabled={!ws || ws.readyState !== WebSocket.OPEN}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Right: Transcripts */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Transcripts</CardTitle>
                  <CardDescription>
                    View conversation history for this agent
                  </CardDescription>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={loadTranscripts}
                  disabled={transcriptsLoading}
                >
                  <Filter className="w-4 h-4 mr-2" />
                  Refresh
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Filters */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Channel</Label>
                  <Select
                    value={channelFilter}
                    onValueChange={(v) =>
                      setChannelFilter(v as "chat" | "voice" | "sms" | "all")
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="chat">Chat</SelectItem>
                      <SelectItem value="voice">Voice</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Sort By</Label>
                  <Select
                    value={timeField}
                    onValueChange={(v) =>
                      setTimeField(
                        v as
                          | "createdAt"
                          | "updatedAt"
                          | "lastMessageAt"
                          | "endedAt"
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="createdAt">Created</SelectItem>
                      <SelectItem value="updatedAt">Updated</SelectItem>
                      <SelectItem value="lastMessageAt">
                        Last Message
                      </SelectItem>
                      <SelectItem value="endedAt">Ended</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Order</Label>
                  <Select
                    value={order}
                    onValueChange={(v) => setOrder(v as "asc" | "desc")}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="desc">Newest First</SelectItem>
                      <SelectItem value="asc">Oldest First</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Limit</Label>
                  <Input
                    type="number"
                    min="1"
                    max="200"
                    value={limit}
                    onChange={(e) =>
                      setLimit(
                        Math.min(
                          200,
                          Math.max(1, parseInt(e.target.value) || 50)
                        )
                      )
                    }
                  />
                </div>
              </div>

              {/* Date Range */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fromDate">From Date</Label>
                  <Input
                    id="fromDate"
                    type="datetime-local"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="toDate">To Date</Label>
                  <Input
                    id="toDate"
                    type="datetime-local"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
              </div>

              {/* Turns Filter */}
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="minTurns"
                  checked={minTurns !== undefined}
                  onCheckedChange={(checked) => {
                    setMinTurns(checked ? 1 : undefined);
                  }}
                />
                <Label
                  htmlFor="minTurns"
                  className="text-sm font-normal cursor-pointer"
                >
                  Show only chats with interactions (turns &gt; 0)
                </Label>
              </div>

              {/* Transcripts List */}
              {transcriptsError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-800 text-sm">{transcriptsError}</p>
                </div>
              )}
              {transcriptsLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading transcripts...
                </div>
              ) : transcripts.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No transcripts found
                </div>
              ) : (
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {transcripts.map((conv) => (
                    <div
                      key={conv.id}
                      className="border rounded-lg p-4 hover:bg-accent cursor-pointer transition-colors"
                      onClick={() => loadSessionDetails(conv.conversationId)}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {conv.channel === "voice" ? (
                            <Phone className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <MessageSquare className="w-4 h-4 text-muted-foreground" />
                          )}
                          <span className="text-sm font-medium">
                            {conv.channel === "voice" ? "Voice" : conv.channel === "sms" ? "SMS" : "Chat"} -{" "}
                            {conv.status || "unknown"}
                          </span>
                        </div>
                        {conv.lastMessageAt && (
                          <span className="text-xs text-muted-foreground">
                            {new Date(conv.lastMessageAt).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {(conv.callerName || callerNumber(conv)) && (
                        <div className="flex items-center gap-2 text-sm mb-2">
                          {conv.callerName && (
                            <span className="font-medium">{conv.callerName}</span>
                          )}
                          {callerNumber(conv) && (
                            <span className="font-mono text-xs text-muted-foreground">
                              {callerNumber(conv)}
                            </span>
                          )}
                        </div>
                      )}
                      {conv.callbackPhone && (
                        <Badge variant="secondary" className="mb-2 font-normal">
                          Callback requested - {conv.callbackPhone}
                        </Badge>
                      )}
                      <div className="text-xs text-muted-foreground space-y-1">
                        {conv.turns !== undefined && (
                          <div>Turns: {conv.turns}</div>
                        )}
                        {conv.durationMs !== undefined && (
                          <div>
                            Duration: {Math.round(conv.durationMs / 1000)}s
                          </div>
                        )}
                        {conv.sessionId && (
                          <div className="font-mono text-[10px] truncate">
                            {conv.sessionId}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Selected Session Details */}
              {selectedSession && selectedSession.messages && (
                <div className="mt-6 border-t pt-6">
                  <h3 className="font-semibold mb-4">Session Messages</h3>
                  <div className="space-y-2 max-h-[400px] overflow-y-auto">
                    {selectedSession.messages.map((msg, idx) => (
                      <div
                        key={msg.id || idx}
                        className="border rounded p-3 text-sm"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium capitalize">
                            {msg.role}
                          </span>
                          {msg.turn !== null && (
                            <span className="text-xs text-muted-foreground">
                              Turn {msg.turn}
                            </span>
                          )}
                        </div>
                        <div className="text-muted-foreground whitespace-pre-wrap break-words">
                          {msg.content}
                        </div>
                        {(msg.model || msg.usage) && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {msg.model && <span>Model: {msg.model}</span>}
                            {msg.model && msg.usage && <span> · </span>}
                            {msg.usage && (
                              <span>
                                Tokens: {msg.usage.prompt_tokens ?? "?"} in /{" "}
                                {msg.usage.completion_tokens ?? "?"} out
                                {msg.usage.total_tokens !== undefined &&
                                  ` (${msg.usage.total_tokens} total)`}
                              </span>
                            )}
                          </div>
                        )}
                        {msg.createdAt && (
                          <div className="text-xs text-muted-foreground mt-1">
                            {new Date(msg.createdAt).toLocaleString()}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <AgentRevisionHistory agentId={agentId} onReverted={loadAgent} />
        </div>
      </div>
    </div>
  );
}
